import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/property.types';

const Navbar: React.FC = () => {
  const { user, logout, canEdit, canViewOwnerData } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);

  const getRoleDisplay = (role: UserRole) => {
    const roleConfig = {
      [UserRole.ADMIN]: { 
        label: 'Administrador', 
        color: 'bg-blue-100 text-blue-800',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM15.657 6.343a1 1 0 011.414 0A9.972 9.972 0 0119 12a9.972 9.972 0 01-1.929 5.657 1 1 0 11-1.414-1.414A7.971 7.971 0 0017 12a7.971 7.971 0 00-1.343-4.243 1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )
      },
      [UserRole.COMERCIAL]: { 
        label: 'Comercial', 
        color: 'bg-orange-100 text-orange-800',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
          </svg>
        )
      },
      [UserRole.RESIDENCIAL]: { 
        label: 'Residencial', 
        color: 'bg-green-100 text-green-800',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
          </svg>
        )
      },
      [UserRole.VENTAS]: { 
        label: 'Ventas', 
        color: 'bg-purple-100 text-purple-800',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
          </svg>
        )
      },
      [UserRole.CALL_CENTER]: { 
        label: 'Call Center', 
        color: 'bg-yellow-100 text-yellow-800',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
        )
      },
      [UserRole.ASESOR]: { 
        label: 'Asesor', 
        color: 'bg-gray-100 text-gray-800',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        )
      }
    };

    return roleConfig[role] || { 
      label: role, 
      color: 'bg-gray-100 text-gray-800', 
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      )
    };
  };

  const getRolePermissions = (role: UserRole) => {
    const permissions = {
      [UserRole.ADMIN]: ['✅ Ve todas las propiedades', '✅ Ve datos de propietarios', '✅ Puede editar/crear/eliminar'],
      [UserRole.COMERCIAL]: ['🏢 Solo propiedades comerciales', '📋 Solo arriendos', '✅ Ve datos de propietarios', '❌ Solo lectura'],
      [UserRole.RESIDENCIAL]: ['🏠 Solo propiedades residenciales', '📋 Solo arriendos', '✅ Ve datos de propietarios', '❌ Solo lectura'],
      [UserRole.VENTAS]: ['✅ Ve todas las propiedades', '💰 Solo ventas', '✅ Ve datos de propietarios', '❌ Solo lectura'],
      [UserRole.CALL_CENTER]: ['✅ Ve todas las propiedades', '❌ NO ve datos de propietarios', '❌ Solo lectura'],
      [UserRole.ASESOR]: ['✅ Ve todas las propiedades', '❌ NO ve datos de propietarios', '❌ Solo lectura']
    };

    return permissions[role] || [];
  };

  if (!user) return null;

  const roleConfig = getRoleDisplay(user.role);
  const permissions = getRolePermissions(user.role);

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo y título */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <h1 className="text-xl font-bold text-gray-900 flex items-center">
                <svg className="w-6 h-6 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
                Sistema de Propiedades
              </h1>
            </div>
          </div>

          {/* Usuario y controles */}
          <div className="flex items-center space-x-4">
            {/* Indicador de permisos */}
            <div className="hidden md:flex items-center space-x-2 text-sm text-gray-600">
              {canEdit && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  Edición
                </span>
              )}
              {canViewOwnerData && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  Propietarios
                </span>
              )}
            </div>

            {/* Información del usuario */}
            <div className="relative group">
              <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setShowDropdown(!showDropdown)}>
                <div className="relative">
                  {/* Usamos directamente el icono del rol en lugar de intentar cargar una imagen */}
                  <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
                    {roleConfig.icon}
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${roleConfig.color}`}>
                      <span className="mr-1">{roleConfig.icon}</span>
                      {roleConfig.label}
                    </span>
                  </div>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Dropdown con información detallada */}
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  {/* Header del dropdown */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
                        {roleConfig.icon}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${roleConfig.color} mt-1`}>
                          <span className="mr-1">{roleConfig.icon}</span>
                          {roleConfig.label}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Permisos del rol */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Permisos del rol:</h4>
                    <div className="space-y-1">
                      {permissions.map((permission, index) => (
                        <div key={index} className="text-xs text-gray-600 flex items-start">
                          <span className="mr-1">•</span>
                          <span>{permission}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Estadísticas rápidas */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Estado actual:</h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-blue-50 rounded p-2">
                        <div className="font-medium text-blue-800">Sesión activa</div>
                        <div className="text-blue-600">Conectado</div>
                      </div>
                      <div className="bg-green-50 rounded p-2">
                        <div className="font-medium text-green-800">Acceso</div>
                        <div className="text-green-600">{canEdit ? 'Escritura' : 'Solo lectura'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Opciones de usuario */}
                  <div className="py-1">
                    <button
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Cerrar Sesión
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;